import { ReactNode, useEffect, useState } from "react";
import { Clock } from "lucide-react";
import Container from "../../components/Container.tsx";
import ContainerHeader from "../../components/ContainerHeader.tsx";
import ActionButton from "../../components/ActionButton.tsx";
import {
  TeacherLessonPrice,
  TeacherLessonPriceType,
  TeacherLessonPricesDto,
  useTeacherLessonPricesMutation,
  useTeacherLessonPricesQuery,
} from "../../api/teacherApi.ts";
import { toast } from "react-hot-toast";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { Tabs } from "../../components/Tabs.tsx";
import Infobox from "../../components/util/InfoBox.tsx";

const durations = [30, 45, 60];

const emptyPrices = (): Record<number, number> => ({ 30: 0, 45: 0, 60: 0 });

export default function TeacherLessonPrices() {
  const { data: existingPrices, isLoading: isLoadingPrices } =
    useTeacherLessonPricesQuery();
  const { mutate: putTeacherLessonPrices, isPending: isSaving } =
    useTeacherLessonPricesMutation();

  const [bookingPrices, setBookingPrices] =
    useState<Record<number, number>>(emptyPrices());
  const [subscriptionPrices, setSubscriptionPrices] =
    useState<Record<number, number>>(emptyPrices());

  useEffect(() => {
    if (existingPrices?.prices) {
      const bMap = emptyPrices();
      const sMap = emptyPrices();
      existingPrices.prices.forEach((p: TeacherLessonPrice) => {
        if (p.type === "SUBSCRIPTION") {
          sMap[p.durationInMinutes] = p.price;
        } else {
          bMap[p.durationInMinutes] = p.price;
        }
      });
      setBookingPrices(bMap);
      setSubscriptionPrices(sMap);
    }
  }, [existingPrices]);

  const handlePriceChange = (
    type: TeacherLessonPriceType,
    duration: number,
    value: string,
  ) => {
    const numericValue = parseFloat(value) || 0;
    if (type === "BOOKING") {
      setBookingPrices((prev) => ({ ...prev, [duration]: numericValue }));
    } else {
      setSubscriptionPrices((prev) => ({ ...prev, [duration]: numericValue }));
    }
  };

  const handleSave = () => {
    const lessonPrices: TeacherLessonPrice[] = [
      ...durations.map((d) => ({
        durationInMinutes: d,
        price: bookingPrices[d],
        currency: "EUR",
        type: "BOOKING" as TeacherLessonPriceType,
      })),
      ...durations.map((d) => ({
        durationInMinutes: d,
        price: subscriptionPrices[d],
        currency: "EUR",
        type: "SUBSCRIPTION" as TeacherLessonPriceType,
      })),
    ];

    const payload: TeacherLessonPricesDto = {
      prices: lessonPrices,
    };

    putTeacherLessonPrices(payload, {
      onSuccess: () => toast.success(t`Lesson prices updated successfully.`),
      onError: (error) =>
        console.error("Failed to update lesson prices:", error),
    });
  };

  if (isLoadingPrices) {
    return (
      <Container className="mx-auto flex w-full max-w-3xl flex-col">
        <ContainerHeader>
          <Trans>Lesson Pricing</Trans>
        </ContainerHeader>
        <div className="flex items-center justify-center py-8">
          <span className="text-soluud-text-secondary text-sm">
            <Trans>Loading prices...</Trans>
          </span>
        </div>
      </Container>
    );
  }

  return (
    <Container className="mx-auto flex w-full max-w-3xl flex-col">
      <ContainerHeader>
        <Trans>Lesson Pricing</Trans>
      </ContainerHeader>

      <Tabs defaultValue="booking">
        <Tabs.Tab label={<Trans>Single Booking</Trans>} value="booking">
          <PriceSection
            description={
              <Trans>
                Set prices for individual lesson bookings. Students can book a
                single lesson at these rates. Not filling in a price will not
                allow the user to select the duration.
              </Trans>
            }
            type="BOOKING"
            prices={bookingPrices}
            onPriceChange={handlePriceChange}
          />
        </Tabs.Tab>
        <Tabs.Tab label={<Trans>Subscription</Trans>} value="subscription">
          <PriceSection
            description={
              <Trans>
                Set monthly subscription prices for regular lessons. Students
                will be charged on a recurring basis. Not filling in a price
                will not allow the user to select the duration.
              </Trans>
            }
            type="SUBSCRIPTION"
            prices={subscriptionPrices}
            onPriceChange={handlePriceChange}
          />
        </Tabs.Tab>
      </Tabs>

      <ActionButton
        variant="primary"
        onClick={handleSave}
        isLoading={isSaving}
        className="mt-4 self-end"
      >
        <Trans>Save</Trans>
      </ActionButton>
    </Container>
  );
}

interface PriceSectionProps {
  description: ReactNode;
  type: TeacherLessonPriceType;
  prices: Record<number, number>;
  onPriceChange: (
    type: TeacherLessonPriceType,
    duration: number,
    value: string,
  ) => void;
}

function PriceSection({
  description,
  type,
  prices,
  onPriceChange,
}: PriceSectionProps) {
  return (
    <div className="flex flex-col gap-4">
      <Infobox>{description}</Infobox>
      <div className="flex flex-col divide-y divide-gray-100">
        {durations.map((duration) => {
          const hasPrice = prices[duration] > 0;
          return (
            <div
              key={duration}
              className="flex items-center gap-4 py-3.5 first:pt-0 last:pb-0"
            >
              <div className="flex min-w-27.5 items-center gap-2">
                <Clock className="text-soluud-primary h-4 w-4 shrink-0" />
                <span className="text-soluud-text-primary text-sm font-medium">
                  {duration} <Trans>min</Trans>
                </span>
              </div>

              <div className="relative flex-1">
                <span className="text-soluud-text-tertiary pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm select-none">
                  €
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={prices[duration] || ""}
                  onChange={(e) =>
                    onPriceChange(type, duration, e.target.value)
                  }
                  placeholder="0.00"
                  className="focus:border-soluud-primary focus:ring-soluud-primary/30 w-full rounded-lg border border-gray-300 py-2 pr-3 pl-7 text-sm transition-colors duration-150 focus:ring-2 focus:outline-none"
                />
              </div>

              <span
                className={`hidden w-24 shrink-0 text-right text-xs sm:block ${hasPrice ? "text-soluud-primary font-medium" : "text-soluud-text-tertiary"}`}
              >
                {hasPrice ? (
                  <Trans>Available</Trans>
                ) : (
                  <Trans>Unavailable</Trans>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
